import React from 'react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ISelectProps, Select } from '../../npaper';
import { useStores } from '../../stores';
import { IPaginationFilter } from '../../stores/utils/modelWithCRUD';
import { useDebouncedCallback } from 'use-debounce/lib';
import {
  ILocation,
  ILocationSnapshotIn,
} from '../../stores/warehousing/LocationStore';

interface ILocationSelectProps
  extends Omit<
    ISelectProps<ILocation>,
    'itemRenderer' | 'placeholder' | 'items' | 'resolveSelectedValue'
  > {
  filter?: IPaginationFilter<ILocationSnapshotIn>['filter'];
}

const LocationSelect: React.FC<ILocationSelectProps> = observer(
  ({ children, selected = null, filter = {}, query: propQuery, ...props }) => {
    const intl = useIntl();
    const { locationStore } = useStores();
    const [opened, setOpened] = useState(false);

    const [items, setItems] = useState<ILocation[]>();
    const [query, setQuery] = useState(propQuery);
    const debouncedQuery = useDebouncedCallback(setQuery, 150);

    useEffect(() => {
      debouncedQuery.callback(propQuery);
    }, [debouncedQuery, propQuery]);

    useEffect(() => {
      if (!opened) return;

      let isSub = true;
      locationStore
        .fetchMore({ filter: { ...filter, search: query } })
        .then((res) => isSub && setItems(res.items));

      return () => {
        isSub = false;
      };
    }, [locationStore, filter, query, opened]);

    return (
      <Select
        {...props}
        value={props.value || undefined}
        items={items}
        isOpen={opened}
        onOpen={() => {
          setOpened(true);
          props.onOpen && props.onOpen();
        }}
        onClose={() => {
          setOpened(false);
          props.onClose && props.onClose();
        }}
        isLoading={!items || locationStore.isLoading}
        resolveSelectedValue={(id) => locationStore.getItem(id)}
        valueRenderer={(item) => item.code}
        itemRenderer={(item) => item.code}
        placeholder={intl.formatMessage({
          defaultMessage: 'Select a location',
        })}
      >
        {children}
      </Select>
    );
  }
);

export default LocationSelect;
